import { useState } from 'react';
import { Users } from 'lucide-react';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { PlatformLogo } from '@/shared/components/promote/PlatformLogo';
import { AdPlatform } from '@/shared/types/enums';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import { useGenerateAudienceEstimate } from '../../../api/campaigns-mutations';
import type { AudienceEstimateRequest } from '../../../types/campaigns.types';
import styles from './AudienceEstimatePanel.module.scss';

function formatNumber(num: number) {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return num.toString();
}

/**
 * Reach estimate pinned to the right of the campaign wizard, mirroring
 * AdPreviewPanel. Unlike a live-updating figure, the estimate is only
 * (re-)generated on demand — Meta and Google both hit a real platform API
 * behind the same button (no local approximation, a missing connection or a
 * platform-side failure surfaces as an error), which is too heavy — and for
 * Google, too slow — to fire on every targeting edit.
 */
export function AudienceEstimatePanel() {
  const platform = useCampaignWizardStore((s) => s.formData.platform);
  const adAccountId = useCampaignWizardStore((s) => s.formData.adAccountId);
  const targetingConfig = useCampaignWizardStore((s) => s.formData.targetingConfig);

  const estimateConfig: AudienceEstimateRequest = {
    platform,
    locations: targetingConfig?.locations,
    ageMin: targetingConfig?.ageMin,
    ageMax: targetingConfig?.ageMax,
    googleAgeRange: targetingConfig?.googleAgeRange,
    genders: targetingConfig?.genders,
    // Behaviors persist alongside interests with type: 'BEHAVIOR' — the
    // backend splits them back out for Meta's targeting spec.
    interests: [...(targetingConfig?.interests ?? []), ...(targetingConfig?.behaviors ?? [])],
  };
  const estimateConfigJson = JSON.stringify(estimateConfig);

  const mutation = useGenerateAudienceEstimate();
  // Tracks the config the *last successful* estimate was generated from, so
  // further targeting edits can be flagged as stale without auto-refetching.
  const [generatedForJson, setGeneratedForJson] = useState<string | null>(null);
  const isStale = generatedForJson !== null && generatedForJson !== estimateConfigJson;
  const estimate = mutation.data;

  const handleGenerate = () => {
    mutation.mutate(estimateConfig, {
      onSuccess: () => setGeneratedForJson(estimateConfigJson),
    });
  };

  const buttonLabel =
    platform === AdPlatform.META
      ? 'Generate Meta Estimate'
      : platform === AdPlatform.GOOGLE
        ? 'Generate Google Forecast'
        : 'Generate Estimate';

  return (
    <aside className={styles.panel} aria-label="Audience estimate">
      <span className={styles.panelLabel}>Estimate</span>
      <AppCard className={styles.estimateCard}>
        <div className={styles.estimateHeader}>
          <Users size={18} />
          <span>Estimated Audience Size</span>
        </div>

        {estimate ? (
          <>
            <span className={styles.estimateValue}>{formatNumber(estimate.estimatedReach)}</span>
            <span className={styles.estimateLabel}>
              potential reach ({formatNumber(estimate.reachLowerBound)}–
              {formatNumber(estimate.reachUpperBound)})
            </span>
            {isStale && (
              <span className={styles.estimateStale}>
                Targeting changed — regenerate for an updated estimate.
              </span>
            )}
          </>
        ) : (
          <span className={styles.estimatePending}>
            Generate an estimate to see your potential reach.
          </span>
        )}

        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleGenerate}
          loading={mutation.isPending}
          disabled={!adAccountId}
          title={!adAccountId ? 'Connect an ad account to generate an estimate' : undefined}
        >
          {platform === AdPlatform.META && (
            <PlatformLogo platform={AdPlatform.META} size={16} label="Meta" />
          )}
          {platform === AdPlatform.GOOGLE && (
            <PlatformLogo platform={AdPlatform.GOOGLE} size={16} label="Google" />
          )}
          {buttonLabel}
        </AppButton>
      </AppCard>
    </aside>
  );
}
